/*
 * File: OrderAppActions.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: The actions for the order app MST store
 */
import { flow, destroy, getEnv } from "mobx-state-tree";
import Api from "../../Api";
import Users from "services/users/Users";
import References from "services/references/References";
import UI from "../UI";
import OrderModel from "../models/Order";
import { parseISO } from "date-fns";

/**
 * Actions for the Order App Store
 *
 * @param {Object} self
 */
const OrderAppActions = self => ({
  afterCreate() {
    self.populateUsers();
    self.populateReferences();
  },

  populateUsers: flow(function*() {
    yield getEnv(self)
      .storeManager.getStore(Users)
      .getProviders();
    yield getEnv(self)
      .storeManager.getStore(Users)
      .getUsers();
  }),

  populateReferences: flow(function*() {
    yield getEnv(self)
      .storeManager.getStore(References)
      .getOffices();
  }),
  /**
   * Fetch an order by id
   *
   * @param {Number} ordersId
   */
  fetch: flow(function*(ordersId) {
    try {
      const result = yield Api.find(ordersId);
      return OrderModel.create(result, getEnv(self));
    } catch (error) {
      console.log("Error fetching order", error);
    }
  }),

  /**
   * Fetch the orders from the api and set the viewModel
   */
  fetchOrders: flow(function*() {
    const filtersPref = getEnv(self).storeManager.getPreference(
      "ordersFilters"
    );
    if (self.ordersFetched) {
      return new Promise(resolve => resolve(self.orders));
    }
    try {
      destroy(self.orders);

      const filterTabValue = getEnv(self).storeManager.getStore(UI)
        .filterTabValue;
      const ordersQueryString = self.buildQueryString(filtersPref);
      const mineOrdersQueryString = self.buildQueryString(filtersPref, "mine");
      const ordersRes = yield Api.fetch(ordersQueryString);
      const mineOrdersRes = yield Api.fetch(mineOrdersQueryString);

      //There's only one "orders" array in the store, only store data for currently selected tab
      if (filterTabValue === "mine") {
        mineOrdersRes.data.forEach(order => {
          if (!order.due) self.orders.push(order);
          else {
            order.due = parseISO(order.due);
            self.orders.push(order);
          }
        });
        self.totalPageCount = Number(mineOrdersRes.headers.pagecount) || 0;
      } else {
        ordersRes.data.forEach(order => {
          if (!order.due) self.orders.push(order);
          else {
            const newDueDate = parseISO(order.due);
            order.due = newDueDate;
            self.orders.push(order);
          }
        });
        self.totalPageCount = Number(ordersRes.headers.pagecount) || 0;
      }
      self.ordersCount = Number(ordersRes.headers["x-total-count"]) || 0;
      self.usersOrdersCount =
        Number(mineOrdersRes.headers["x-total-count"]) || 0;
      self.pageNumber = 1;
      self.ordersFetched = true;
    } catch (error) {
      console.log("Error fetching orders...", error);
    }
  }),

  loadMoreItems: flow(function*() {
    if (!self.hasNextPage) return;

    const filtersPref = getEnv(self).storeManager.getPreference(
      "ordersFilters"
    );
    self.isNextPageLoading = true;
    try {
      const uiStore = getEnv(self).storeManager.getStore(UI);
      const filterTabValue = uiStore.filterTabValue;
      const queryString = self.buildQueryString(filtersPref, filterTabValue);
      const res = yield Api.fetch(queryString);
      const orders = res.data || [];
      if (orders.length > 0 && uiStore.filterTabValue === filterTabValue) {
        orders.forEach(order => {
          if (!order.due) self.orders.push(order);
          else {
            const newDueDate = parseISO(order.due);
            order.due = newDueDate;
          }
        });
        self.orders.push(...orders);
      }
      self.pageNumber = self.pageNumber + 1;
      self.isNextPageLoading = false;
      self.ordersFetched = true;
    } catch (error) {
      console.log("Error fetching orders...", error);
    }
  }),

  /**
   * Resets store props and fetches the orders again
   */
  refreshOrders: flow(function*() {
    destroy(self.orders);
    self.orders = undefined;
    self.ordersFetched = false;
    self.isNextPageLoading = false;
    self.pageNumber = 0;
    self.totalPageCount = 0;
    return yield self.fetchOrders();
  }),

  /**
   * Adds a new order to the start of the orders array
   *
   * @param {Object} order
   */
  create(order) {
    self.orders.unshift(order);
  },

  /**
   * Updates an order in the orders array by record id
   *
   * @param {Object} order
   */
  update(order) {
    const idx = self.orders.findIndex(o => o.ordersId === order.ordersId);
    self.orders[idx] = order;
  }
});

export default OrderAppActions;
